import axios from 'axios';

const LOG_STYLE = 'color: #888; font-weight: bold;';
const REQ_STYLE = 'color: #2196F3; font-weight: bold;';
const RES_STYLE = 'color: #4CAF50; font-weight: bold;';
const ERR_STYLE = 'color: #f44336; font-weight: bold;';

let requestCounter = 0;

// ── Axios interceptors ──────────────────────────────────────────────

const axiosRequestInterceptor = axios.interceptors.request.use(
  (config) => {
    const id = ++requestCounter;
    config._debugId = id;
    config._debugStart = performance.now();

    console.groupCollapsed(`%c[REQ #${id}] %c${config.method?.toUpperCase()} ${config.url}`, LOG_STYLE, REQ_STYLE);
    console.log('URL:', config.baseURL ? `${config.baseURL}${config.url}` : config.url);
    console.log('Method:', config.method?.toUpperCase());
    console.log('Headers:', { ...config.headers });
    if (config.data instanceof FormData) {
      const entries = {};
      config.data.forEach((value, key) => {
        entries[key] = value instanceof File ? `[File: ${value.name}, ${value.size} bytes, ${value.type}]` : value;
      });
      console.log('Body (FormData):', entries);
    } else if (config.data) {
      console.log('Body:', config.data);
    }
    console.log('Full config:', config);
    console.groupEnd();

    return config;
  },
  (error) => {
    console.error('%c[REQ ERROR]', ERR_STYLE, error);
    return Promise.reject(error);
  }
);

const axiosResponseInterceptor = axios.interceptors.response.use(
  (response) => {
    const id = response.config?._debugId || '?';
    const duration = response.config?._debugStart
      ? `${(performance.now() - response.config._debugStart).toFixed(0)}ms`
      : '';

    console.groupCollapsed(
      `%c[RES #${id}] %c${response.status} ${response.config?.method?.toUpperCase()} ${response.config?.url} %c${duration}`,
      LOG_STYLE, RES_STYLE, LOG_STYLE
    );
    console.log('Status:', response.status, response.statusText);
    console.log('Headers:', { ...response.headers });
    console.log('Data:', response.data);
    console.groupEnd();

    return response;
  },
  (error) => {
    const id = error.config?._debugId || '?';
    const duration = error.config?._debugStart
      ? `${(performance.now() - error.config._debugStart).toFixed(0)}ms`
      : '';

    console.groupCollapsed(
      `%c[RES #${id}] %c${error.response?.status || 'NETWORK ERROR'} ${error.config?.method?.toUpperCase()} ${error.config?.url} %c${duration}`,
      LOG_STYLE, ERR_STYLE, LOG_STYLE
    );
    console.log('Status:', error.response?.status, error.response?.statusText);
    console.log('Headers:', error.response?.headers ? { ...error.response.headers } : null);
    console.log('Data:', error.response?.data);
    console.log('Error:', error.message);
    console.groupEnd();

    return Promise.reject(error);
  }
);

// ── Fetch wrapper ────────────────────────────────────────────────────

const originalFetch = window.fetch;

window.fetch = async function debugFetch(input, init = {}) {
  const id = ++requestCounter;
  const method = (init.method || 'GET').toUpperCase();
  const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input?.url || String(input);
  const start = performance.now();

  console.groupCollapsed(`%c[FETCH REQ #${id}] %c${method} ${url}`, LOG_STYLE, REQ_STYLE);
  console.log('URL:', url);
  console.log('Method:', method);
  if (init.headers) console.log('Headers:', init.headers);
  if (init.body instanceof FormData) {
    const entries = {};
    init.body.forEach((value, key) => {
      entries[key] = value instanceof File ? `[File: ${value.name}, ${value.size} bytes, ${value.type}]` : value;
    });
    console.log('Body (FormData):', entries);
  } else if (init.body) {
    console.log('Body:', init.body);
  }
  console.groupEnd();

  try {
    const response = await originalFetch.call(this, input, init);
    const duration = `${(performance.now() - start).toFixed(0)}ms`;

    console.groupCollapsed(
      `%c[FETCH RES #${id}] %c${response.status} ${method} ${url} %c${duration}`,
      LOG_STYLE,
      response.ok ? RES_STYLE : ERR_STYLE,
      LOG_STYLE
    );
    console.log('Status:', response.status, response.statusText);
    console.log('Headers:', Object.fromEntries(response.headers.entries()));
    console.log('(body will be consumed by caller)');
    console.groupEnd();

    return response;
  } catch (error) {
    const duration = `${(performance.now() - start).toFixed(0)}ms`;
    console.groupCollapsed(`%c[FETCH RES #${id}] %cNETWORK ERROR ${method} ${url} %c${duration}`, LOG_STYLE, ERR_STYLE, LOG_STYLE);
    console.log('Error:', error.message);
    console.groupEnd();
    throw error;
  }
};

export function disableRequestLogger() {
  axios.interceptors.request.eject(axiosRequestInterceptor);
  axios.interceptors.response.eject(axiosResponseInterceptor);
  window.fetch = originalFetch;
}
